import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { JsonSyntaxDisplay } from "./JsonSyntaxDisplay";

interface JsonCollapsibleContentProps {
  data: unknown;
  showRaw: boolean;
  maxInlineSize: number;
  defaultExpanded?: boolean;
}

export function JsonCollapsibleContent({
  data,
  showRaw,
  maxInlineSize,
  defaultExpanded = false,
}: JsonCollapsibleContentProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const jsonString = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return "[Invalid JSON Object]";
    }
  }, [data]);

  const isLarge = jsonString.length > maxInlineSize;

  if (isLarge) {
    return (
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-between px-2 py-0 h-6 gap-1 flex"
          >
            <span className="text-xs text-muted-foreground">
              {isExpanded ? "Collapse" : "Expand"} JSON ({jsonString.length}{" "}
              chars)
            </span>
            {Array.isArray(data) && (
              <Badge variant="outline" className="ml-2 text-xs">
                Array({data.length})
              </Badge>
            )}
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-2">
          <div className="bg-muted/20 p-2 rounded border">
            <JsonSyntaxDisplay data={data} showRaw={showRaw} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className="bg-muted/20 p-2 rounded border">
      <JsonSyntaxDisplay data={data} showRaw={showRaw} />
    </div>
  );
}
